/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Spin, Progress, Menu, Dropdown, message as $message } from 'antd'
import { useMemoizedFn } from 'ahooks'
import classNames from 'classnames'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import Loading3QuartersOutlined from '@ant-design/icons/Loading3QuartersOutlined'
import PlayCircleFilled from '@ant-design/icons/PlayCircleFilled'
import { sortableHandle } from 'react-sortable-hoc'
import { manager } from '@/modules/goods/utils/imageEdit'
import CustomIcon from '@/components/CustomIcon'
import { createAPI } from '@/utils/modal'
import { queryVideoDetailBySpecialVideoLink } from '@/services/alioss'
import { wrapResponseData } from '@/utils/wrapRequest'
import Cropper from './Cropper'
import { isVideo } from './utils'
import { singleImageValidateError, transErrorsToMsg, isAutoReplaceImage } from './helper'
import styles from './Preview.less'
import { getFileCover, checkIsSupportReadVideoPoster, checkIsFakeLink } from '@/utils/qiniu'
import { AUTO_PEPLACE_TYPE } from './constants'
import AiFullImages from './AiFullImages'

const SortableHandle = sortableHandle(({ children }) => children)

const Cover = React.memo(({ dataSource, thumbnail, onPreview, onFailure, draggable, ...restProps }) => {
    const [info, setInfo] = useState(null)

    const isVideoFile = isVideo(dataSource)

    const isError = dataSource.status === 'error' || dataSource.status === 'failurePic'

    const loadUrl = useMemoizedFn(async () => {
        const data = await wrapResponseData(() =>
            queryVideoDetailBySpecialVideoLink({
                specialVideoLink: dataSource.url
            })
        )

        if (data?.videoUrl) {
            setInfo(data)
        } else {
            onFailure && onFailure(dataSource)
        }
    })

    const url = dataSource.url || ''

    useEffect(() => {
        if (checkIsFakeLink(url)) {
            loadUrl()
        }
    }, [url])

    const handleFailure = () => {
        onFailure && onFailure(dataSource)
    }

    const handleOnload = e => {
        if (isVideoFile) {
            return
        }

        if (e.currentTarget.naturalWidth === 1) {
            // alicdn 图片失效 (404) 会返回一个1*1的图片信息 不会触发onerror事件 所以这里根据图片width为1判断图片失效
            onFailure && onFailure(dataSource)
        }
    }

    const img = (
        <div>
            <figure itemProp="associatedMedia" itemScope>
                <a
                    href={dataSource.url}
                    itemProp="contentUrl"
                    onClick={e => e.preventDefault()}
                    className={classNames({
                        [styles.dropdownDraggable]: draggable
                    })}
                >
                    <img
                        onError={handleFailure}
                        onLoad={handleOnload}
                        src={
                            getFileCover(dataSource.url, {
                                width: thumbnail.width * 2,
                                height: thumbnail.height * 2
                            }) || dataSource.thumbnailUrl
                        }
                        {...restProps}
                        style={{
                            objectFit: 'cover',
                            width: `${thumbnail.width - 2}px`,
                            height: `${thumbnail.height - 2}px`
                        }}
                        width={thumbnail.width - 2}
                        height={thumbnail.height - 2}
                        referrerPolicy="no-referrer"
                        alt=""
                    />
                </a>
                <figcaption itemProp="caption description" />
            </figure>
        </div>
    )

    if (isVideoFile) {
        const renderPoster = () => {
            // 假链接，抖音链接访问需要把请求标头中的referrer置为空，referrerPolicy（https://www.geeksforgeeks.org/html-referrerpolicy-attribute/）
            if (checkIsFakeLink(dataSource.url)) {
                return (
                    <a href={info?.videoUrl} target="_blank" referrerPolicy="no-referrer" rel="noopener noreferrer">
                        <img
                            src={info?.videoCoverUrl}
                            {...restProps}
                            style={{
                                objectFit: 'cover',
                                width: `${thumbnail.width - 2}px`,
                                height: `${thumbnail.height - 2}px`
                            }}
                            width={thumbnail.width - 2}
                            height={thumbnail.height - 2}
                            alt=""
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                        />
                    </a>
                )
            }

            // 存在缩略图或者支持读取封面的地址则直接显示图片标签保持原样
            if (dataSource.thumbnailUrl || checkIsSupportReadVideoPoster(dataSource.url)) {
                return (
                    <div>
                        <figure itemProp="associatedMedia" itemScope>
                            <a
                                href={dataSource.url}
                                itemProp="contentUrl"
                                onClick={e => e.preventDefault()}
                                className={classNames({
                                    [styles.dropdownDraggable]: draggable
                                })}
                            >
                                <img
                                    src={
                                        dataSource.thumbnailUrl ||
                                        getFileCover(dataSource.url, {
                                            width: thumbnail.width * 2,
                                            height: thumbnail.height * 2
                                        })
                                    }
                                    {...restProps}
                                    style={{
                                        objectFit: 'cover',
                                        width: `${thumbnail.width - 2}px`,
                                        height: `${thumbnail.height - 2}px`
                                    }}
                                    alt=""
                                    referrerPolicy="no-referrer"
                                    onError={handleFailure}
                                />
                            </a>
                            <figcaption itemProp="caption description" />
                        </figure>
                    </div>
                )
            }

            // 其他则显示video以显示三方链接视频的封面
            return (
                <video
                    preload={false}
                    controls={false}
                    autoPlay={false}
                    crossOrigin="anonymous"
                    src={dataSource.url}
                    style={{ objectFit: 'cover' }}
                    width={thumbnail.width - 2}
                    height={thumbnail.height - 2}
                    onError={handleFailure}
                />
            )
        }

        const renderMask = () => {
            if (isError) {
                return null
            }

            // 假链接
            if (checkIsFakeLink(dataSource.url)) {
                return (
                    <div className={styles.overlay} style={{ pointerEvents: 'none' }}>
                        <PlayCircleFilled className={styles.play} />
                    </div>
                )
            }

            if (!draggable) {
                return (
                    <div
                        onClick={e => {
                            e.stopPropagation()

                            if (checkIsFakeLink(dataSource.url)) {
                                // 使用实际信息预览
                                if (info) {
                                    onPreview({
                                        ...dataSource,
                                        url: info.videoUrl
                                    })
                                }
                            } else {
                                onPreview(dataSource)
                            }
                        }}
                        className={classNames(styles.videoMask, { [styles.bottom0]: isVideo(dataSource) })}
                    >
                        <div className={styles.overlay}>
                            <PlayCircleFilled className={styles.play} />
                        </div>
                    </div>
                )
            }

            return (
                <div
                    className={styles.overlay}
                    onClick={e => {
                        e.stopPropagation()

                        if (checkIsFakeLink(dataSource.url)) {
                            // 使用实际信息预览
                            if (info) {
                                onPreview({
                                    ...dataSource,
                                    url: info.videoUrl
                                })
                            }
                        } else {
                            onPreview(dataSource)
                        }
                    }}
                >
                    <PlayCircleFilled className={styles.play} />
                </div>
            )
        }

        return (
            <>
                {renderPoster()}
                {renderMask()}
            </>
        )
    }

    return img
})

const ThreeRowsOperate = React.memo(({ handleCrop, handleCopy, dataSource }) => {
    return (
        <div className={styles.threeRows}>
            <div
                className={styles.cropHandle}
                onClick={() => {
                    handleCrop(dataSource)
                }}
            >
                <CustomIcon className={styles.crop} type="icon-caijian" />
            </div>
            <div
                className={styles.copyHandle}
                onClick={() => {
                    handleCopy(dataSource)
                }}
            >
                <CustomIcon className={styles.copy} type="icon-a-fuzhishangpinziliao" />
            </div>
            <div className={styles.eyeHandle}>
                <CustomIcon className={styles.eye} type="icon-chakandatu" />
            </div>
        </div>
    )
})

const Operate = React.memo(({ handleCrop, dataSource }) => {
    return (
        <>
            <div
                className={styles.cropHandle}
                onClick={() => {
                    handleCrop(dataSource)
                }}
            >
                <CustomIcon className={styles.crop} type="icon-caijian" />
            </div>
            <div className={styles.eyeHandle}>
                <CustomIcon className={styles.eye} type="icon-chakandatu" />
            </div>
        </>
    )
})

const EditOperate = React.memo(({ isRecover, sizeSmall, handleEdit, handleRecover, dataSource }) => {
    if (isRecover) {
        return (
            <>
                <div
                    data-log-full-text="编辑单张图片"
                    className={classNames(styles.editHandleLeft)}
                    onClick={() => {
                        handleEdit(dataSource)
                    }}
                >
                    编辑
                </div>
                <div
                    data-log-full-text="恢复单张图片"
                    className={classNames(styles.editHandleRight)}
                    onClick={() => {
                        handleRecover(dataSource)
                    }}
                >
                    恢复
                </div>
            </>
        )
    }

    return (
        <div
            data-log-full-text="编辑单张图片"
            className={classNames(styles.editHandle, {
                [styles.hasTopRadius]: sizeSmall
            })}
            onClick={() => {
                handleEdit(dataSource)
            }}
        >
            {!sizeSmall ? '编辑图片' : '编辑'}
        </div>
    )
})

const BootmOperate = React.memo(
    ({
        isRecover,
        dataSource,
        showCopy,
        sizeSmall,
        imageEditable,
        handleEdit,
        handleCrop,
        handleRecover,
        handleCopy
    }) => {
        if (isVideo(dataSource)) {
            return null
        }

        if (showCopy) {
            return (
                <>
                    {imageEditable && (
                        <EditOperate
                            isRecover={isRecover}
                            sizeSmall={sizeSmall}
                            handleEdit={handleEdit}
                            handleRecover={handleRecover}
                            dataSource={dataSource}
                        />
                    )}
                    <ThreeRowsOperate handleCrop={handleCrop} handleCopy={handleCopy} dataSource={dataSource} />
                </>
            )
        }

        return (
            <>
                {imageEditable && (
                    <EditOperate
                        isRecover={isRecover}
                        sizeSmall={sizeSmall}
                        handleEdit={handleEdit}
                        handleRecover={handleRecover}
                        dataSource={dataSource}
                    />
                )}
                <Operate handleCrop={handleCrop} dataSource={dataSource} />
            </>
        )
    }
)

const RenderContent = React.memo(
    ({
        index,
        isRecover,
        dataSource,
        showProgress,
        handleEdit,
        handleCrop,
        handlePreview,
        handleRecover,
        handleCopy,
        thumbnail,
        draggable,
        disabled,
        onFailure,
        imageEditable,
        showCopy,
        customRender
    }) => {
        const { status, location } = dataSource

        const showPreview = useMemo(() => {
            let showPreview = null
            if (!showProgress || isVideo(dataSource)) {
                showPreview = status === 'done' || status === 'error' || status === 'failurePic'
            } else {
                showPreview =
                    status === 'previewed' ||
                    status === 'uploading' ||
                    status === 'done' ||
                    status === 'error' ||
                    status === 'failurePic'
            }
            return showPreview
        }, [dataSource, showProgress, status])

        if (showPreview) {
            const menu = (
                <Menu>
                    <Menu.Item
                        className={styles.menu}
                        key="edit"
                        data-log-full-text="编辑单张图片"
                        onClick={() => handleEdit(dataSource)}
                    >
                        编辑
                    </Menu.Item>
                    <Menu.Item className={styles.menu} key="crop" onClick={() => handleCrop(dataSource)}>
                        裁切
                    </Menu.Item>
                    <Menu.Item className={styles.menu} key="preview" onClick={() => handlePreview(dataSource)}>
                        查看
                    </Menu.Item>
                    {isRecover && (
                        <Menu.Item className={styles.menu} key="recover" onClick={() => handleRecover(dataSource)}>
                            恢复
                        </Menu.Item>
                    )}
                </Menu>
            )

            const sizeSmall = !(thumbnail?.width && thumbnail.width > 60)

            return (
                <>
                    {location && <span className={styles.location}>主图{location}</span>}

                    {customRender && customRender({ index })}

                    {status !== 'error' && (
                        <Dropdown overlay={menu} trigger={['contextMenu']}>
                            <Cover
                                dataSource={dataSource}
                                thumbnail={thumbnail}
                                onPreview={handlePreview}
                                onFailure={onFailure}
                                draggable={draggable}
                            />
                        </Dropdown>
                    )}

                    {status === 'uploading' && (
                        <Progress
                            className={styles.progress}
                            percent={dataSource.percent}
                            strokeWidth={2}
                            size="small"
                            status="active"
                            showInfo={false}
                        />
                    )}

                    {draggable && !disabled && (
                        <SortableHandle>
                            <div
                                className={classNames(styles.dragItem, {
                                    [styles.hasEdit]: imageEditable,
                                    [styles.bottom0]:
                                        isVideo(dataSource) ||
                                        dataSource.status === 'status' ||
                                        dataSource.status === 'failurePic'
                                })}
                            />
                        </SortableHandle>
                    )}

                    {status !== 'error' && status !== 'failurePic' ? (
                        <BootmOperate
                            isRecover={isRecover}
                            dataSource={dataSource}
                            showCopy={showCopy}
                            sizeSmall={sizeSmall}
                            imageEditable={imageEditable}
                            handleEdit={handleEdit}
                            handleCrop={handleCrop}
                            handleCopy={handleCopy}
                            handleRecover={handleRecover}
                        />
                    ) : null}

                    {dataSource.message ? <span className={styles.tip}>{dataSource.message}</span> : null}
                </>
            )
        } else {
            return <Spin indicator={<Loading3QuartersOutlined style={{ fontSize: 23, color: '#666' }} spin />} />
        }
    }
)

export default ({
    index,
    dataSource,
    showProgress,
    showCopy,
    cropRecSizes,
    disabled,
    thumbnail,
    enablePreview = true,
    imageEditable = false,
    imageLabel,
    style,
    moving,
    onDelete,
    onPreview,
    draggable,
    onFailure,
    onCrop,
    onCopy,
    onEdit,
    singleValidate,
    customRender,
    form,
    ...restProps
}) => {
    const [aiFullImagesList, setAiFullImagesList] = useState([])
    const cacheUrl = useRef(null)

    const isRecover = useMemo(() => {
        const isAutoReplace = isAutoReplaceImage({
            url: dataSource?.url,
            aiFullImagesList
        })

        if (isAutoReplace) {
            if (isAutoReplace?.oldUrl) {
                cacheUrl.current = isAutoReplace?.oldUrl
            }
            return isAutoReplace
        }
        cacheUrl.current = dataSource?.url
        return false
    }, [dataSource?.url, aiFullImagesList])

    // ai填充更新
    const handleAiFullImagesUpdate = useMemoizedFn(list => {
        setAiFullImagesList(list)
    })

    // 裁切弹窗
    const handleCrop = target => {
        const { url, status } = target
        if (status && (status === 'error' || status === 'failurePic')) {
            $message.error('图片失效或错误无法进行图片裁剪~')
            return
        }
        const { close } = createAPI(Cropper, {
            dataSource: {
                ...restProps,
                ...target,
                src: url,
                cropRecSizes
            },
            onOk: data => {
                onCrop &&
                    onCrop({
                        ...dataSource,
                        ...data
                    })
                close()
            }
        })
    }

    const handleEdit = target => {
        if (!target?.url) {
            return
        }

        const newImages = [
            {
                name: imageLabel,
                uid: target.uid,
                url: target.url
            }
        ]

        manager.open({
            multiple: false,
            images: newImages,
            recommendedSizes: cropRecSizes
                ?.map?.(_ => {
                    let [width, height] = _.label.split('x')
                    width = Number(width)
                    height = Number(height)
                    if (Number.isNaN(width) || Number.isNaN(height)) {
                        return null
                    }
                    return { width, height }
                })
                .filter(Boolean),
            onChange(e) {
                if (Array.isArray(e) && e.length) {
                    onEdit && onEdit(e[0])
                }
            }
        })
    }

    // 复制弹窗
    const handleCopy = target => {
        onCopy && onCopy({ ...target })
    }

    const handlePreview = target => {
        if (!enablePreview) {
            return
        }

        if (typeof onPreview === 'function') {
            onPreview(target)
            return
        }

        if (target.url) {
            window.open(target.url, '_blank')
        }
    }

    const handleDelete = () => {
        if (dataSource?.uploadTask) {
            dataSource?.uploadTask?.abort?.()
        }

        onDelete(dataSource)
    }

    const handleRecover = () => {
        onEdit({
            ...dataSource,
            url: cacheUrl.current
        })
    }

    const { isError, errors } = useMemo(() => {
        return singleImageValidateError({
            singleValidate,
            data: dataSource
        })
    }, [dataSource, singleValidate])

    const errorMsg = useMemo(() => {
        return transErrorsToMsg(errors)
    }, [errors])

    return (
        <div
            className={classNames({
                [styles.isRecoverContainer]: !isError && isRecover,
                [styles.isErrorContainer]: isError
            })}
        >
            <div
                className={classNames(styles.container, {
                    [styles.isDisabled]: disabled,
                    [styles.isMoving]: moving
                })}
                style={style}
            >
                <div className={styles.content}>
                    <RenderContent
                        index={index}
                        isRecover={isRecover}
                        dataSource={dataSource}
                        showProgress={showProgress}
                        handleEdit={handleEdit}
                        handleCrop={handleCrop}
                        handlePreview={handlePreview}
                        handleCopy={handleCopy}
                        handleRecover={handleRecover}
                        thumbnail={thumbnail}
                        draggable={draggable}
                        disabled={disabled}
                        onFailure={onFailure}
                        imageEditable={imageEditable}
                        showCopy={showCopy}
                        customRender={customRender}
                    />
                </div>

                {!disabled ? (
                    <div className={styles.delete}>
                        <CloseCircleFilled className={styles.deleteIcon} onClick={handleDelete} />
                    </div>
                ) : null}
            </div>
            {singleValidate && (
                <div className={styles.errorContainer}>{isError && <div className={styles.error}>{errorMsg}</div>}</div>
            )}
            {!isError && isRecover && (
                <div className={styles.recoverContainer}>
                    <CustomIcon className={styles.icon} type="icon-wancheng" />
                    {isRecover.type === AUTO_PEPLACE_TYPE.AUTO && '已修正'}
                    {isRecover.type === AUTO_PEPLACE_TYPE.AI && (
                        <>
                            <img
                                className={styles.autoAi}
                                src="https://assets.sursung.com/activity/20250616/41156EE0C5904D21AF545F32FA369FBF1750057102230.png"
                            />
                            {dataSource?.recoverText || '已修正'}
                        </>
                    )}
                </div>
            )}

            <AiFullImages onUpdate={handleAiFullImagesUpdate} />
        </div>
    )
}
